export interface PlayLiveAudioOptions {
  volume?: number;
  /** Parent to mount a detached element into (defaults to document.body). */
  parent?: HTMLElement;
}

/** Ensure the element is iOS-ready (hidden, playsinline) and attached to the DOM. */
function mountLiveAudioElement(element: HTMLAudioElement, parent?: HTMLElement): void {
  if (typeof document === "undefined" || element.isConnected) {
    return;
  }
  element.style.display = "none";
  element.setAttribute("playsinline", "");
  (parent ?? document.body).appendChild(element);
}

/** Create a hidden, iOS-friendly audio element for WebRTC playback. */
export function createLiveAudioElement(parent?: HTMLElement): HTMLAudioElement {
  const element = document.createElement("audio");
  element.autoplay = true;
  element.setAttribute("playsinline", "");
  element.style.display = "none";
  mountLiveAudioElement(element, parent);
  return element;
}

/**
 * Return the existing element (re-attaching it if detached) or create a new one.
 * Reusing one persistent element keeps its iOS gesture-unlock alive across reconnects.
 */
export function getOrCreateLiveAudioElement(
  existing: HTMLAudioElement | null,
  parent?: HTMLElement,
): HTMLAudioElement {
  if (!existing) {
    return createLiveAudioElement(parent);
  }
  mountLiveAudioElement(existing, parent);
  return existing;
}

/**
 * Unlock an audio element inside a user gesture by starting muted playback.
 *
 * iOS/WebKit only honors `play()` while a user activation is live. The egress
 * pipeline performs several network round-trips before audio arrives, which
 * consumes that activation. Call this synchronously from the tap handler BEFORE
 * any async work; `playLiveAudioElement` later unmutes the already-playing
 * element (which no longer needs a fresh gesture).
 */
export function primeLiveAudioElement(element: HTMLAudioElement): void {
  mountLiveAudioElement(element);
  element.muted = true;
  void element.play().catch(() => {
    /* may still be blocked without a gesture; harmless */
  });
}

/**
 * Mount a WebRTC audio element if needed and start (or unmute) playback.
 * iOS Safari requires playsinline, DOM attachment, and an explicit play() after user gesture.
 */
export async function playLiveAudioElement(
  element: HTMLAudioElement,
  options: PlayLiveAudioOptions = {},
): Promise<void> {
  mountLiveAudioElement(element, options.parent);

  if (options.volume != null) {
    element.volume = options.volume;
  }

  element.muted = false;

  if (!element.paused) {
    return;
  }

  await element.play();
}
